import React, {
	useCallback, useEffect, useRef, useState
} from 'react';
import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';

type LazyImageProps = {
	src: string;
	alt: string;
	className?: string;
	rootMargin?: string;
	innerRef?: React.Ref<Element>;
};

function LazyImage ({
	src, alt, className, rootMargin = '0px', innerRef, ...rest
}: LazyImageProps & React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>
& React.HTMLAttributes<HTMLElement>) {

	const srcTransparentGif = '/img/global/trans.gif';
	const ref = useRef<Element>();
	const [ inViewRef, inView ] = useInView({
		rootMargin,
		triggerOnce: true
	});

	const setRefs = useCallback(
		(node: Element) => {

			ref.current = node;

			if (innerRef) {

				if (typeof innerRef === 'function') {

					innerRef(node);

				} else {

					(innerRef as React.MutableRefObject<Element | null>).current = node;

				}
				
			}

			inViewRef(node);

		},
		[ inViewRef, innerRef ]
	);

	const [ imgLoaded, setImgLoaded ] = useState(false);

	useEffect(() => {

		const element = ref.current;
		const handleLoad = () => {

			setImgLoaded(true);
			if (element) {

				element.removeEventListener('load', handleLoad);

			}

		};
		if (inView && element) {

			element.addEventListener('load', handleLoad);

		}

		return () => {

			if (element) {

				element.removeEventListener('load', handleLoad);

			}

		};

	}, [ inView, ref, setImgLoaded ]);

	const fullClassName = classNames(className, { skeleton: !imgLoaded && inView });

	return <img className={fullClassName} alt={alt} ref={setRefs} src={inView ? src : srcTransparentGif} {...rest} />;

}

export default LazyImage;
